import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { FaFilePdf, FaFileWord, FaCaretDown, FaCheck, FaCheckCircle, FaExclamationTriangle, FaTrash } from 'react-icons/fa'
import { Stack, ResponsiveStack, Textarea, Heading, Text, Badge, Button, Box, Progress, CircularProgress, Spinner, Alert, SimpleModal, Select, SimpleAccordion, Icon, List, ConfirmButton, SimpleMenu, RichTextViewer, VideoViewer } from '@/components/lib'
import api from '../../../api'
import utils, { buildComponentVisibilityMap, componentConditionIsMet } from '../../../utils/utils'
import useStore from '../../../store'

import ShortText from '../../fields/ShortText'
import LongText from '../../fields/LongText'
import Integer from '../../fields/Integer'
import Float from '../../fields/Float'
import SliderField from '../../fields/Slider'
import Choice from '../../fields/Choice'
import DateInput from '../../fields/Date'
import DatetimeInput from '../../fields/Datetime'
import TimeInput from '../../fields/Time'
import Temperature from '../../fields/Temperature'
import Height from '../../fields/Height'
import Weight from '../../fields/Weight'
import BMI from '../../fields/BMI'
import RAVLT from '../../fields/RAVLT'
import File from '../../fields/File'
import Image from '../../fields/Image'
import Video from '../../fields/Video'
import Food from '../../fields/Food'
import PromisForm from '../../includes/PromisForm'
import PromisBattery from '../../includes/PromisBattery'
import TeethChart from '../../fields/TeethChart'

const FIELDS = {
  text: ShortText,
  paragraph: LongText,
  integer: Integer,
  float: Float,
  slider: SliderField,
  choice: Choice,
  date: DateInput,
  datetime: DatetimeInput,
  time: TimeInput,
  temperature: Temperature,
  height: Height,
  weight: Weight,
  bmi: BMI,
  file: File,
  image: Image,
  video: Video,
  food: Food,
  ravlt: RAVLT,
  promisForm: PromisForm,
  promisBattery: PromisBattery,
  teethChart: TeethChart
}

export default function ResponseEditor ({ trialId, stageId, stageRecurrence, participantId, isOpen, onClose, onSubmit, previewComponents }) {
  const [stage, setStage] = useState()
  const [components, setComponents] = useState([])
  const [participant, setParticipant] = useState()
  const [responses, setResponses] = useState([])
  const [drafts, setDrafts] = useState([])
  const [response, setResponse] = useState({})
  const [updateNote, setUpdateNote] = useState('')
  const [existingResponse, setExistingResponse] = useState({})
  const [recurrence, setRecurrence] = useState(0)
  const [changesMade, setChangesMade] = useState(false)
  const [error, setError] = useState()
  const [errorField, setErrorField] = useState()
  const [exporting, setExporting] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { trial, dataTypes } = useStore()
  const isPreview = previewComponents?.length > 0
  const modalContentRef = useRef()
  // Tracks whether the response has been submitted, so a debounced draft save
  // that fires after submission can be ignored instead of resurrecting a draft.
  const submittedRef = useRef(false)

  useEffect(() => {
    // Always reset recurrence when (re)opening the editor. For one-off stages we
    // pass 0 so we don't accidentally "remember" a prior recurring selection.
    setRecurrence(stageRecurrence ?? 0)
  }, [stageRecurrence, stageId])

  // Reset the submission guard whenever the editor (re)opens or the target
  // stage/recurrence changes, so a fresh form can save drafts and submit again.
  useEffect(() => {
    submittedRef.current = false
    setSubmitting(false)
  }, [isOpen, stageId, recurrence])

  // If this is a preview, then load the preview components
  useEffect(() => {
    if (previewComponents?.length) {
      setComponents(previewComponents)
    }
  }, [previewComponents])

  // Fetch the responses when the stageId or participantId changes
  useEffect(() => {
    if (stageId && participantId) {
      api.trials.getParticipantResponses(trialId, participantId, ({ drafts, responses, participant }) => {
        setDrafts(drafts)
        setResponses(responses)
        setParticipant(participant)
      })
    }
  }, [stageId, participantId])

  // Fetch the stage when the stageId changes
  useEffect(() => {
    if (stageId) {
      api.stages.get(stageId, setStage)
    }
  }, [stageId])

  // Build a component list based on the data type objects
  useEffect(() => {
    if (stage?.components?.length && dataTypes?.length) {
      setComponents(stage.components.map(c => {
        const dt = dataTypes.find(d => d._id === c.dataType)
        if (dt) return { ...c, type: dt }
        return c
      }))
    }
  }, [stage, dataTypes])

  // If stage/recurrence change, reload the existing/draft response for this stage/recurrence
  useEffect(() => {
    if (stage?.isRecurring && !stage?.adHoc) { // Recurring stage. Grab the response with stage and recurrence
      const stageResponsesForRecurrence = responses?.filter(r => r.stage === stage?._id && ((r.stageRecurrence || 0) === (recurrence || 0)))
      if (stageResponsesForRecurrence?.length && stageResponsesForRecurrence[0]?.response) setExistingResponse(stageResponsesForRecurrence[0]?.response)
      else setExistingResponse({})
    }
    if (stage?.isAnchored && !stage?.adHoc) { // Anchored stage slot. Grab the response with stage and slot index
      const stageResponsesForSlot = responses?.filter(r => r.stage === stage?._id && ((r.stageRecurrence || 0) === (recurrence || 0)))
      if (stageResponsesForSlot?.length && stageResponsesForSlot[0]?.response) setExistingResponse(stageResponsesForSlot[0]?.response)
      else setExistingResponse({})
    } else if (!stage?.isRecurring && !stage?.adHoc) { // One-off stage. Grab the response matching the stage
      const stageResponses = responses?.filter(r => r.stage === stage?._id && !r.stageRecurrence)
      if (stageResponses?.length && stageResponses[0]?.response) setExistingResponse(stageResponses[0]?.response)
      else setExistingResponse({})
    }
    if (stage?.adHoc) setExistingResponse({}) // Ad-hoc stage. Create a new response.

    const stageDraft = drafts?.find(r => r.stage === stage?._id && (r.stageRecurrence || 0) === (recurrence || 0))?.response
    setResponse(stageDraft ?? {})
  }, [recurrence, stage, responses])

  // Create a new draft with the response argument
  const saveDraft = useCallback(utils.debounce((resp) => {
    // The response has already been submitted (the draft was consumed). Saving
    // now would recreate a stale draft that gets reloaded next time, so bail.
    if (submittedRef.current) return
    setError()
    const stageIdWithRecurrence = `${stage._id}${stage.isAnchored ? `::t${recurrence || 0}` : (recurrence > 0 ? `::${recurrence}` : '')}`
    api.trials.updateParticipantResponseDraft(trialId, participant._id, stageIdWithRecurrence, { response: resp }, newDraft => {
      setChangesMade(false)

      // Save draft locally to prevent re-fetching
      let found = false
      const newDrafts = drafts?.map(d => {
        if (d.stage === newDraft.stage && (d.stageRecurrence || 0) === (newDraft.stageRecurrence || 0)) {
          found = true
          return newDraft
        }
        return d
      })
      if (!found) {
        newDrafts.push(newDraft)
      }
      setDrafts(newDrafts)
    }, err => {
      setError(err.message)
    })
  }), [stage, recurrence, participant, drafts]) // Refresh function if drafts change (important)

  // Delete the draft for this stage and reset responses
  const revertDraft = () => {
    if (isPreview) {
      setResponse({})
      setChangesMade(false)
      return
    }
    const stageIdWithRecurrence = `${stage._id}${stage.isAnchored ? `::t${recurrence || 0}` : (recurrence > 0 ? `::${recurrence}` : '')}`
    api.trials.deleteParticipantResponseDraft(trialId, participant._id, stageIdWithRecurrence, () => {
      setDrafts(prev =>
        (prev || []).filter(d => !(
          d.stage === stage._id &&
          (d.stageRecurrence || 0) === (recurrence || 0)
        ))
      )
      setResponse({})
      setChangesMade(false)
      setError()
    }, err => {
      utils.toast('error', 'Unable to delete the draft', err.message)
      setError(err.message)
    })
  }

  // Send data off to submit for real
  const submit = () => {
    // Guard against double submits and against submitting while a draft save is
    // still pending/in-flight (a later save would resurrect a stale draft).
    if (submitting || changesMade) return
    setError()
    setErrorField()
    setSubmitting(true)
    submittedRef.current = true
    const stageId = `${stage._id}${stage.isAnchored ? `::t${recurrence || 0}` : (recurrence > 0 ? `::${recurrence}` : '')}`
    api.trials.updateParticipantResponse(trialId, participant._id, stageId, { response, updateNote }, () => {
      // Clear any cached PROMIS form/battery progress for this participant so
      // reopening the editor starts a fresh questionnaire rather than showing
      // the just-submitted completed state.
      try {
        Object.keys(window.sessionStorage)
          .filter(k => (k.startsWith('promis_session_') || k.startsWith('promis_battery_')) && k.includes(participant._id))
          .forEach(k => window.sessionStorage.removeItem(k))
      } catch { /* sessionStorage unavailable */ }
      setUpdateNote('')
      onSubmit()
      onClose()
      utils.toast('success', 'The participant\'s data has been updated')
    }, err => {
      // Submission failed — let the user keep editing and retry.
      submittedRef.current = false
      setSubmitting(false)
      if (err.message.indexOf('[') > -1) {
        const match = err.message.match(/\[([a-z0-9-]+)\]/i)
        if (match?.length > 1) {
          setErrorField(match[1])
          err.message = err.message.replace('[' + match[1] + ']', '')
          document.getElementById(`field-entry-${match[1]}`)?.scrollIntoView({ behavior: 'smooth' })
        }
      }
      setError(err.message)
    })
  }

  function fieldHasValue (dataTypeId) {
    const value = response[dataTypeId]?.value || existingResponse[dataTypeId]?.value
    if (!value) return false
    if (Array.isArray(value)) return value.length > 0
    return true
  }

  // Build component visibility map to handle chained conditionals
  // This processes components in order and accounts for dependencies (C depends on B depends on A)
  const componentVisibilityMap = useMemo(() => {
    return buildComponentVisibilityMap(components, response, existingResponse)
  }, [components, response, existingResponse])

  function conditionIsMet (c) {
    return componentConditionIsMet(c, componentVisibilityMap)
  }

  function exportStage (format) {
    setExporting(true)
    const responseExport = responses?.filter(r => r.stage === stage?._id && ((r.stageRecurrence || 0) === (recurrence || 0)))[0]
    api.stages.getWithFormatAndResponse(stageId, format, responseExport?._id ?? '', pdf => {
      const link = document.createElement('a')
      link.href = pdf?.url
      link.target = '_blank'
      link.setAttribute('download', `${stage.name}.pdf`)
      document.body.appendChild(link)
      link.click()
      setExporting(false)
    }, err => {
      utils.toast('error', 'Unable to export stage', err.message)
      setExporting(false)
    })
  }

  function closeModal () {
    if (changesMade) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close this window?')) onClose()
    } else {
      onClose()
    }
  }

  // Extract some useful objects for this stage
  const stageResponses = responses?.filter(r => r?.stage === stage?._id)
  const stageResponsesForRecurrence = stageResponses?.filter(r => (r.stageRecurrence || 0) === (recurrence || 0))

  // Process sections separated by sectionDivider components
  const sections = []
  components?.forEach((c, i) => {
    if (c.type === 'sectionDivider') {
      // Calculate percentage complete
      let sectionFields = 0
      let completedSectionFields = 0
      for (let j = i + 1; j < components.length; j++) {
        const c2 = components[j]
        if (!c2 || c2.type === 'sectionDivider') break
        else if (c2.dataType && !c2.condition) {
          const dt = dataTypes?.find(d => d._id === c2.dataType)
          if (dt?.type !== 'calculation') {
            sectionFields += 1
            if (fieldHasValue(c2.dataType)) completedSectionFields += 1
          }
        }
      }
      sections.push({ title: c.title, id: c.id, amountCompleted: sectionFields > 0 ? (completedSectionFields / sectionFields) : 1 })
    }
  })

  // Calculate percentage of the total form completed (calculation types excluded)
  let totalFields = 0
  let completedFields = 0
  components?.forEach(c => {
    if (c.dataType && !c.condition) {
      const dt = dataTypes?.find(d => d._id === c.dataType)
      if (dt?.type !== 'calculation') {
        totalFields += 1
        if (fieldHasValue(c.dataType)) completedFields += 1
      }
    }
  })
  const amountCompleted = totalFields > 0 ? (completedFields / totalFields) : 1
  const noteRequired = trial.signOffNoteRequired || (!stage?.adHoc && ((!stage?.isRecurring && stageResponses?.length > 0) || (stage?.isRecurring && stageResponsesForRecurrence?.length > 0)))
  return (
    <SimpleModal
      contentRef={modalContentRef}
      size='4xl'
      isOpen={isOpen} onClose={closeModal}
      header={`Add data for ${stage?.name || 'stage'}`}
      body={
        <Stack gap={5}>

          <ResponsiveStack gap={5} justifyContent='space-between' alignItems='center' bg='bg.subtle' p={3} rounded='md'>
            <Stack>
              {stage?.adHoc
                ? <Alert status='info' variant='left-accent'>This stage is ad-hoc. You can submit as many responses to it as needed.</Alert>
                : (
                  <>
                    {stage?.isAnchored && (
                      <>
                        <Alert status='info' variant='left-accent'>You are adding data for slot {recurrence + 1} of this anchored stage.</Alert>
                        {stageResponsesForRecurrence?.length > 0 &&
                          <Alert status='warning' variant='left-accent' fontSize='sm'>Data has already been submitted for this slot of this stage. Any changes you submit below will overwrite the existing submitted data for this slot.</Alert>}
                      </>
                    )}
                    {!stage?.isAnchored && !stage?.isRecurring && stageResponses?.length > 0 &&
                      <Alert status='warning' variant='left-accent'>This stage is not recurring and already has sumbmitted data. This means that any data you submit here will overwrite existing submitted data for this stage.</Alert>}
                    {stage?.isRecurring &&
                      <>
                        {stageRecurrence == null
                          ? (
                            <Box>
                              <Heading size='sm'>Since this is a repeating (recurring) stage, choose a recurrence to add data for</Heading>
                              {stage?.isPoll && <Text fontSize='sm' color='fg.muted'>Note: this stage has "poll mode" enabled. Data will not be overwritten even if re-editing the same recurrence.</Text>}
                              <Select bg='bg.surface' placeholder='Select a recurrence' value={recurrence} onChange={e => setRecurrence(parseInt(e.target.value) || 0)}>
                                {[...Array(stage?.recurringDuration || 0)].map((i, index) =>
                                  <option value={index} key={index}>
                                    {index + 1}
                                    {stageResponses?.filter(r => (r.stageRecurrence || 0) === (index || 0))?.length > 0 && ' (data has been submitted)'}
                                  </option>
                                )}
                              </Select>
                            </Box>)
                          : (
                            <Alert status='info' variant='left-accent'>You are adding data for recurrence {stageRecurrence + 1} of this stage.</Alert>)}
                        {stageResponsesForRecurrence?.length > 0 &&
                          <Alert status='warning' variant='left-accent' fontSize='sm'>Data has already been submitted for this recurrence of this stage. Any changes you submit below will overwrite the existing submitted data for this recurrence.</Alert>}
                      </>}
                  </>)}
            </Stack>
            <Box>
              {stageId &&
                <SimpleMenu
                  contentProps={{ portalRef: modalContentRef }}
                  trigger={<Button isLoading={exporting} size='sm'>Download form <Icon as={FaCaretDown} /></Button>}
                  items={[
                    { icon: FaFilePdf, text: 'Download in PDF format', onClick: () => exportStage('pdf') },
                    { icon: FaFileWord, text: 'Download as a Word document', onClick: () => exportStage('docx') }
                  ]}
                />}
            </Box>
          </ResponsiveStack>

          <Box position='relative'>
            <ResponsiveStack direction='row' gap={5} position='relative'>

              {sections?.length > 0 &&
                <Stack gap={4} minW={250} maxW={350} p={4} shadow='md' bg='bg.subtle' rounded='md' style={{ top: 30 }}>
                  <Stack direction='row' alignItems='center' justifyContent='space-between'>
                    <Heading size='xs' textTransform='uppercase' color='fg.muted'>Sections</Heading>
                    <Progress w='100px' flex={1} bg='bg.muted' colorPalette={amountCompleted === 1 ? 'green' : 'orange'} value={amountCompleted * 100} size='xs' />
                  </Stack>

                  <Stack gap={2} maxH='550px' overflowY='auto' position='sticky' style={{ top: 10 }}>
                    {sections.map(section =>
                      <Stack
                        direction='row' alignItems='center' justifyContent='space-between'
                        key={section.id}
                        p={2} bg='bg.muted' _hover={{ bg: 'bg.subtle' }} cursor='pointer' rounded='md'
                        onClick={e => document.querySelector(`#component-${section.id}`)?.scrollIntoView({ behavior: 'smooth' })}
                      >
                        <Text>{section.title}</Text>
                        <CircularProgress
                          value={section.amountCompleted * 100} size='sm'
                          label={section.amountCompleted === 1 ? <Icon as={FaCheckCircle} height='20px' width='20px' color='green.500' /> : (section.amountCompleted === 0 ? <Icon as={FaExclamationTriangle} height='20px' width='20px' color='orange.500' /> : null)}
                        />
                      </Stack>
                    )}
                  </Stack>
                </Stack>}

              <Stack flex={1} spacing={5}>
                {components?.map(c => {
                  if (!conditionIsMet(c)) return null
                  const VideoElement = c.video?.url ? <VideoViewer url={c.video.url} isProcessing={c.video.needsVideoProcess} hasError={c.video.processError} /> : null

                  if (c.type === 'title') {
                    return (
                      <Box key={`component-${c.id}`} p={3} mt={10} mb={10} id={`component-${c.id}`}>
                        <Heading size='lg'>{c.title}</Heading>
                        {VideoElement}
                      </Box>
                    )
                  }
                  if (c.type === 'sectionDivider') {
                    return (
                      <Stack key={`component-${c.id}`} bg='bg.accent' rounded='md' shadow='sm' p={3} mt={10} mb={10} id={`component-${c.id}`}>
                        <Heading size='lg'>{c.title}</Heading>
                        <Box>
                          <RichTextViewer content={c.description} />
                        </Box>
                        {VideoElement}
                      </Stack>
                    )
                  }
                  if (c.type === 'scheduledNotification') {
                    return (
                      <Box p={3} key={`component-${c.id}`} mt={10} mb={10} id={`component-${c.id}`} bg='bg.accentSubtle' rounded='md'>
                        <Text>Set a timer for {c.notificationDelay} seconds before continuing.</Text>
                      </Box>
                    )
                  }
                  if (c.type?.type === 'calculation') return null
                  const Field = FIELDS[c.type?.type]

                  const value = response[c.dataType]?.value ?? existingResponse[c.dataType]?.value
                  const hasChangedValue = response[c.dataType]?.value ?? null
                  const hasValue = fieldHasValue(c.dataType)

                  const onChange = v => {
                    const newResponse = { ...response }
                    const isEmpty = v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
                    if (isEmpty) {
                      delete newResponse[c.dataType]
                    } else {
                      newResponse[c.dataType] = { value: v, createdAt: new Date() }
                    }
                    setResponse(newResponse)
                    if (!isPreview) {
                      if (Object.keys(newResponse).length === 0) {
                        const hasDraft = drafts?.some(d => d.stage === stage?._id && (d.stageRecurrence || 0) === (recurrence || 0))
                        if (hasDraft) revertDraft()
                        else setChangesMade(false)
                      } else {
                        saveDraft(newResponse)
                        setChangesMade(true)
                      }
                    }
                  }

                  return (
                    <Stack gap={3} key={c.id} p={3} borderWidth='1px' rounded='md' bg={errorField === c.id ? 'red.100' : 'bg.surface'} id={`field-entry-${c.id}`}>
                      <Stack direction='row' justifyContent='end'>
                        {hasChangedValue !== null
                          ? <Badge colorPalette='green' size='sm'><Icon as={FaCheck} /> This change will be included when submitted</Badge>
                          : <Badge colorPalette='blue' size='sm'>No changes made</Badge>}
                        {c.isRequired && <Badge colorPalette={hasValue ? 'green' : 'red'} size='sm'>{hasValue && <Icon as={FaCheckCircle} color='green' />} Required field</Badge>}
                      </Stack>
                      <Heading size='md'>{c.title}</Heading>
                      <RichTextViewer content={c.description} />
                      {VideoElement}
                      <Box>
                        {Field
                          ? <Field dataType={c.type} value={value} onChange={onChange} participant={participant} />
                          : <Alert variant='left-accent'>This type of question can't be answered here.</Alert>}
                      </Box>
                    </Stack>
                  )
                })}
              </Stack>
            </ResponsiveStack>

            <Stack gap={3} position='sticky' mt={50} style={{ bottom: 10 }} boxShadow='md' p={3} rounded='md' bg='bg.subtle'>
              {error &&
                <Alert status='error' title='There was a problem storing the stage data'>
                  {error}
                </Alert>}

              <Stack direction={{ base: 'column', lg: 'row' }} spacing={3} justifyContent='end'>
                <Box flex={1}>
                  <SimpleAccordion
                    items={[
                      {
                        label: (
                          <Stack direction='row' alignItems='center'>
                            <Heading size='xs'>Sign-off note</Heading>
                            {noteRequired && !updateNote && <Badge colorPalette='red' size='sm'>Required</Badge>}
                            {updateNote && <Badge colorPalette='green' size='sm'><Icon as={FaCheck} />Added</Badge>}
                          </Stack>),
                        content: (
                          <Textarea bg='bg.surface' size='sm' rows={1} placeholder='Write a note, justification, or information about this update...' value={updateNote} onChange={e => setUpdateNote(e.target.value)} />)
                      }]}
                  />
                </Box>

                <Box display='flex' justifyContent='end' alignItems='center'>
                  {changesMade &&
                    <Stack direction='row' alignItems='center'>
                      <Text>Saving...</Text>
                      <Spinner />
                    </Stack>}
                  {Object.keys(response || {}).length > 0 &&
                    <ConfirmButton
                      trigger={<Button size='sm' variant='ghost'><Icon><FaTrash /></Icon></Button>}
                      header='Really delete your changes?'
                      body={
                        <Text>Your progress will be lost and the form will be reset to the last submitted state.</Text>
                        }
                      onConfirm={revertDraft}
                      actionColor='red'
                    />}

                  <Button size='sm' variant='ghost' onClick={closeModal}>Close for now</Button>

                  <ConfirmButton
                    header='Ready to submit?'
                    body={
                      <Stack gap={3}>
                        <Text>The following data will be recorded for this participant.</Text>
                        <Box p={3} bg='bg.surface' rounded='md' maxH={200} overflowY='auto'>
                          <List
                            items={Object.keys(response || {}).map(key => {
                              const fc = components.filter(c => c.dataType === key)
                              if (fc) return { content: fc[0]?.title }
                              return null
                            })}
                          />
                        </Box>
                        {noteRequired && !updateNote && <Alert status='warning'>A sign-off note is required before submitting.</Alert>}
                      </Stack>
                      }
                    trigger={<Button size='sm' colorPalette='primary' isLoading={submitting} disabled={isPreview || changesMade || submitting || Object.keys(response || {}).length === 0}>Submit {Object.keys(response || {}).length} changes</Button>}
                    onConfirm={submit}
                    actionColor='primary'
                  />
                </Box>
              </Stack>
            </Stack>

          </Box>
        </Stack>
}
    />
  )
}
